import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { useOnboardingStore } from "@/stores/onboarding";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/onboarding/carousel")({
  head: () => ({
    meta: [{ title: "How it works" }],
  }),
  component: CarouselStep,
});

function CarouselStep() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setStep = useOnboardingStore((s) => s.setStep);
  const [index, setIndex] = useState(0);

  const slides = [
    { title: t("onboarding.carousel.slide1Title"), body: t("onboarding.carousel.slide1Body") },
    { title: t("onboarding.carousel.slide2Title"), body: t("onboarding.carousel.slide2Body") },
    { title: t("onboarding.carousel.slide3Title"), body: t("onboarding.carousel.slide3Body") },
  ];

  const slide = slides[index] ?? slides[0]!;
  const last = index === slides.length - 1;

  return (
    <div className="flex flex-1 flex-col">
      <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.18em] text-primary">
        {t("onboarding.carousel.eyebrow")}
      </p>
      <div className="min-h-[180px] rounded-lg border border-border bg-card p-5">
        <h1 className="font-display text-2xl font-bold">{slide.title}</h1>
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{slide.body}</p>
      </div>

      <div className="mt-5 flex justify-center gap-2">
        {slides.map((_, i) => (
          <button
            key={i}
            type="button"
            aria-label={`Slide ${i + 1}`}
            onClick={() => setIndex(i)}
            className={cn(
              "h-2 w-2 rounded-full transition-colors",
              i === index ? "bg-primary" : "bg-muted-foreground/30",
            )}
          />
        ))}
      </div>

      <div className="mt-auto flex gap-2 pt-10">
        <Button
          variant="outline"
          className="flex-1"
          onClick={() => {
            if (index === 0) void navigate({ to: "/onboarding/welcome" });
            else setIndex((v) => v - 1);
          }}
        >
          {t("common.back")}
        </Button>
        <Button
          className="flex-1"
          onClick={() => {
            if (!last) {
              setIndex((v) => v + 1);
              return;
            }
            setStep("verify");
            void navigate({ to: "/signup" });
          }}
        >
          {last ? t("onboarding.carousel.cta") : t("common.next")}
        </Button>
      </div>
    </div>
  );
}
